import classNames from 'classnames';
import { func, object, shape, string } from 'prop-types';
import React, { useCallback, useContext } from 'react';
import { Input } from '@eika/input';
import { Checkbox } from '@eika/checkbox';
import { IconButton } from '@eika/button';
import { IconTypes } from '@eika/icon';
import DropDown from '../../../drop-down';
import CountryContext from '../../../country-context';
import connect from '../../helper/connect';
import getLabel, { getHelpBody } from '../labels';
import ErrorMessage from '../../errormessage';

function hasCountryError(question) {
    return (question.land && question.land.length === 0) || question.land === 'Velg';
}

function hasSkattenummerError(question) {
    return question.skattenummer.length === 0;
}

function hasCompleteValues(question) {
    if (hasCountryError(question)) {
        return false;
    }
    if (question.manglerSkattenummer) {
        return question.arsak.length > 0;
    }
    return !hasSkattenummerError(question);
}

function CRSLand({ onRemove, onChange, question, ekstratekster, context }) {
    const countryContext = useContext(CountryContext);
    const handleRemove = () => onRemove(question.id);

    const getHandleChange = useCallback(
        (key, data = 'value') =>
            (event) => {
                const value = event?.target?.[data] ?? event;
                const updatedQuestion = { ...question, [key]: value };
                onChange(question.id, { [key]: value, error: !hasCompleteValues(updatedQuestion) });
            },
        [onChange, question]
    );

    const hasError = !hasCompleteValues(question);
    const hasInvalidValue = context.getHasSubmitted() && hasError;
    const crsOptions = countryContext.getCRSOptions();
    return (
        <div className={classNames('crs-answer crs-country-dropdown', { 'has-error error': hasInvalidValue })}>
            <IconButton icon={IconTypes.KRYSS_24} className="sgw-button removeicon" onClick={handleRemove} />
            <DropDown
                id="crs-hvilket-land"
                values={crsOptions}
                selectValue={getHandleChange('land')}
                selected={question.land || question.country || ''}
                modalHelpHeader={ekstratekster.crs_land_infotittel}
                modalHelpBody={getHelpBody(ekstratekster, 'crs_land_info')}
                removeValue={handleRemove}
                label={getLabel(ekstratekster, 'crs_land')}
            />
            <div
                className={classNames('skjema-input-field', 'skjema-checkbox', { 'has-error error': hasInvalidValue })}
            >
                <Checkbox
                    onChange={getHandleChange('manglerSkattenummer', 'checked')}
                    name="crs-mangler-manglerSkattenummer"
                    checked={question.taxNumberMissing}
                    id={`mangler-skattenummer_${question.id}`}
                    label={getLabel(ekstratekster, 'crs_mangler')}
                />
            </div>
            <>
                {question.manglerSkattenummer ? (
                    <div className={classNames({ 'has-error error': hasInvalidValue })}>
                        <Input
                            onChange={getHandleChange('arsak')}
                            type="text"
                            name="skattenummerMangler"
                            id={`skattenummerMangler_${question.id}`}
                            value={question.arsak}
                            className="form-control crs-skattenummer"
                            label={getLabel(ekstratekster, 'crs_arsak')}
                        />
                    </div>
                ) : (
                    <div className={classNames({ 'has-error error': hasInvalidValue })}>
                        <Input
                            onChange={getHandleChange('skattenummer')}
                            type="text"
                            name="skattenummer"
                            id={`skattenummer_${question.id}`}
                            value={question.skattenummer}
                            className="form-control crs-skattenummer"
                            label={getLabel(ekstratekster, 'crs_nr')}
                        />
                    </div>
                )}
            </>
            {hasInvalidValue && <ErrorMessage text={getLabel(ekstratekster, 'crs_instruks')} />}
        </div>
    );
}

CRSLand.propTypes = {
    question: object.isRequired,
    ekstratekster: shape({
        crs_land: string,
        crs_land_infotittel: string,
        crs_land_info: string,
        crs_nr: string,
        crs_mangler: string,
        crs_arsak: string,
        crs_instruks: string,
    }).isRequired,
    onChange: func,
    onRemove: func,
    context: object,
};

export default connect(CRSLand);
